import { useEffect, useState } from "react";
import { AddContentImport } from "./AddContentImport";
import { AddContentProcess } from "./AddContentProcess";
import { VerticalSpacing } from "../../VerticalSpacing";
import { V3HeadlineH5 } from "../../Typography";
import { ProgressBar } from "../../ProgressBar/ProgressBar";
import {
    ContentDetailType,
    Email,
    Image as ImageContentType,
    Website,
    ContentType,
    UrlParser,
    ContentResponse,
    FindContentMetadataResponse,
    ImportResponse,
    WebsiteResponse,
    CreateMediaUploadBodyParams,
    CustomMediaAssetType,
    MediaUploadResponse,
    CustomAudio,
    Article,
    CustomVideo,
    FindDuplicatesResponse
} from "@authory-packages/core";
import { ManualImportPathTaken, UniversalAddTrackingEventNames } from "../UniversalAddContentTracking";
import { FindDuplicateParams, findContentByURL } from "../../../utils/findContentByURL";
import { ImportingContentLoader } from "../../ImportingContentLoader";
import { ImportProgressWithUrlWrapper } from "./AddContent.styles";
import { V3BodyGrey35 } from "../../ReusableStyledComponents/V3BodyGrey35";
import { InlineLink } from "../../ReusableStyledComponents/InlineLink";
import { getContentCopyLabel } from "../../../utils/article-text-utils";
import { FileUploadTarget, FinalImportResult, ImportItem } from "./AddContent.types";
import { getTargetContentTypeBasedonManualImportFlow, removeFileExtension } from "./AddContent.utils";
import { AddContentResultContainer } from "./AddContentResultContainer";
import { DuplicateCheck } from "./DuplicateCheck";

const MAX_CONCURRENT_FETCHES = 3;

export enum AddContentWizardStage {
    AddContentImport = "AddContentImport",
    AddContentProcess = "AddContentProcess",
    AddContentComplete = "AddContentComplete"
}

export interface AddContentStageChange {
    stage: AddContentWizardStage;
    /** The wide, half-split edit preview screen is up */
    isConfirmingItem: boolean;
    /** The duplicate screen is up, which uses the narrow single column layout */
    isCheckingDuplicate: boolean;
}

interface AddContentProps {
    sources: WebsiteResponse[];
    isPDFURLImport: boolean;
    isSnippetImport?: boolean;
    suggestedUrl?: string;
    targetContentType: ContentType;
    importPathTaken: ManualImportPathTaken;
    importItems?: ImportItem[];
    startingStage?: AddContentWizardStage;
    itemAdded: ContentResponse[];
    onCloseHandler: () => void;
    onContentAddComplete: () => void;
    onBackHandler: () => void;
    onStageChange: (data: AddContentStageChange) => void;
    onFindContent: (url: string, mimeType?: string) => Promise<ImportResponse | undefined>;
    onWebsiteImport: (url: string) => Promise<ImportResponse | undefined>;
    onFindContentSnippets: (url: string) => Promise<FindContentMetadataResponse | undefined>;
    onFindDuplicate?: (params: FindDuplicateParams) => Promise<FindDuplicatesResponse | undefined>;
    /** Queues a re-import of the existing item. Resolves with it on success, undefined when the call failed. */
    onReplaceDuplicate?: (item: ContentResponse) => Promise<ContentResponse | undefined>;
    onManualImportSubmit: (data: FinalImportResult) => Promise<ContentResponse | undefined>;
    onFileChoosenHandler: (file: File) => Promise<ImportResponse> | undefined;
    onTemporaryImageUploadHandler: (file: File) => Promise<string>;
    trackEvent: (eventName: string, data: any) => void;
    onGetMediaUploadSignedUrl: (data: CreateMediaUploadBodyParams) => Promise<MediaUploadResponse | undefined>;
    onJumpToPDF: () => void;
    usedPlugin: boolean;
}

export const AddContent = ({
    sources,
    isPDFURLImport,
    isSnippetImport,
    targetContentType,
    importPathTaken,
    suggestedUrl = "",
    importItems,
    itemAdded,
    startingStage = AddContentWizardStage.AddContentImport,
    usedPlugin,
    onJumpToPDF,
    onCloseHandler,
    onBackHandler,
    onStageChange,
    onWebsiteImport,
    onManualImportSubmit,
    onFileChoosenHandler,
    onFindContent,
    onFindContentSnippets,
    onFindDuplicate,
    onReplaceDuplicate,
    onGetMediaUploadSignedUrl,
    onTemporaryImageUploadHandler,
    trackEvent,
    onContentAddComplete
}: AddContentProps) => {
    const sourcesOptions: { value: string; text: string | null; url: string }[] = sources.map((src) => ({
        value: src.canonicalSlug,
        text: src.publicationName,
        url: src.url || ""
    }));
    const [itemsToImport, setItemsToImport] = useState<ImportItem[]>([]);
    const [itemsFetching, setItemsFetching] = useState<ImportItem[]>([]);
    const [itemsFetched, setItemsFetched] = useState<ImportItem[]>([]);
    const [stage, setStage] = useState(startingStage);
    const [itemsCompleted, setItemsCompleted] = useState(0);
    const [itemsTotal, setItemsTotal] = useState(0);
    const [showUploadProgressBar, setUploadProgressBar] = useState(false);
    const [fileUploadTotal, setFileUploadTotal] = useState(0);
    const [fileUploadDone, setFileUploadDone] = useState(0);
    const [duplicatesResolved, setDuplicatesResolved] = useState<string[]>([]);

    let currProcessingItem = itemsFetched.length ? itemsFetched[0] : undefined;

    // Items with a duplicate get the duplicate screen first, until the user picks "add as a new item"
    const currDuplicate =
        currProcessingItem && currProcessingItem.duplicate && !duplicatesResolved.includes(currProcessingItem.id)
            ? currProcessingItem.duplicate
            : undefined;

    // Drives both the modal layout and the progress bar width, so the two can't drift apart
    const isHalfSplitLayout = !!currProcessingItem && !currDuplicate;
    const isCheckingDuplicate = !!currDuplicate;

    useEffect(() => {
        if (stage === AddContentWizardStage.AddContentProcess) {
            onStageChange({
                stage: AddContentWizardStage.AddContentProcess,
                isConfirmingItem: isHalfSplitLayout,
                isCheckingDuplicate
            });
        }
    }, [currProcessingItem, isCheckingDuplicate]);

    const startBackgroundFetch = (items: ImportItem[]) => {
        setItemsToImport(items);
        setItemsCompleted(0);
        setItemsTotal(items.length);
        setStage(AddContentWizardStage.AddContentProcess);
        onStageChange({ stage: AddContentWizardStage.AddContentProcess, isConfirmingItem: false, isCheckingDuplicate: false });
    };

    // Drops the item currently being confirmed and moves the queue on to the next one
    const advanceToNextItem = () => {
        if (!currProcessingItem) return;

        const { id } = currProcessingItem;

        setItemsFetched((its) => its.filter((it) => it.id !== id));
        setItemsCompleted((completed) => completed + 1);
        setDuplicatesResolved((ids) => ids.filter((resolvedId) => resolvedId !== id));
    };

    useEffect(() => {
        if (stage === AddContentWizardStage.AddContentComplete) return;
        if (importItems && importItems.length) startBackgroundFetch(importItems);
    }, [importItems]);

    const updateQueue = (newItem: ImportItem) => {
        setItemsFetching((its) => its.filter((it) => it.id !== newItem.id));
        setItemsFetched((its) => [...its, newItem]);
    };

    useEffect(() => {
        const fetchImportResponse = async (item: ImportItem) => {
            if (!item) return;

            const isItemTargetPDF = item?.fileUploadTarget === FileUploadTarget.PDF;
            const isItemTargetWebsite = item?.fileUploadTarget === FileUploadTarget.Website;
            const isItemTargetEmail = item?.fileUploadTarget === FileUploadTarget.Email;
            const isItemTargetImage = item?.fileUploadTarget === FileUploadTarget.Image;
            const isItemTargetCustomVideo = item?.fileUploadTarget === FileUploadTarget.CustomVideo;
            const isItemTargetCustomAudio = item?.fileUploadTarget === FileUploadTarget.CustomAudio;

            try {
                if (item.url && isItemTargetWebsite) {
                    // Website import via URL

                    const importRes = await onWebsiteImport(item.url!);

                    const newItem: ImportItem = {
                        ...item,
                        url: null,
                        contentRes: importRes
                    };

                    updateQueue(newItem);
                } else if (item.url) {
                    const { contentRes, snippetRes, duplicate } = await findContentByURL(
                        onFindContent,
                        onFindContentSnippets,
                        item.url,
                        targetContentType,
                        isSnippetImport,
                        isPDFURLImport,
                        trackEvent,
                        onFindDuplicate
                    );

                    if (duplicate) {
                        trackEvent(UniversalAddTrackingEventNames.DuplicateFound, {
                            slug: duplicate.slug,
                            type: duplicate.type
                        });
                    }

                    const newItem: ImportItem = {
                        ...item,
                        contentRes,
                        snippetRes,
                        duplicate
                    };

                    updateQueue(newItem);
                } else if ((isItemTargetCustomVideo || isItemTargetCustomAudio) && "files" in item && item.files?.length) {
                    // Custom audio / video
                    const customAssetFile = item.files[0];

                    const fileName = removeFileExtension(customAssetFile.name);

                    const params: CreateMediaUploadBodyParams = {
                        title: fileName,
                        assetType: isItemTargetCustomAudio ? CustomMediaAssetType.Audio : CustomMediaAssetType.Video
                    };

                    const uploadLinkres = await onGetMediaUploadSignedUrl(params);

                    if (uploadLinkres) {
                        try {
                            await fetch(uploadLinkres.url, {
                                method: "PUT",
                                body: customAssetFile,
                                headers: { "content-type": customAssetFile.type }
                            });
                        } catch (error) {
                            console.error(error);
                        }
                    }

                    let content: CustomAudio | CustomVideo | Article;

                    if (uploadLinkres) {
                        if (isItemTargetCustomAudio) {
                            content = {
                                type: ContentDetailType.CustomAudio,
                                html: "",
                                uploadId: uploadLinkres.uploadId
                            } as unknown as CustomAudio;
                        } else {
                            content = {
                                type: ContentDetailType.CustomVideo,
                                html: "",
                                uploadId: uploadLinkres.uploadId
                            } as unknown as CustomVideo;
                        }
                    } else {
                        if (isItemTargetCustomAudio) {
                            content = {
                                type: ContentDetailType.Article,
                                html: ""
                            } as unknown as Article;
                        } else {
                            content = {
                                type: ContentDetailType.Article,
                                html: ""
                            } as unknown as Article;
                        }
                    }

                    const importRes: ImportResponse = {
                        content,
                        type: isItemTargetCustomAudio ? ContentType.Audio : ContentType.Video,
                        url: null,
                        date: null,
                        title: fileName || "",
                        text: null,
                        source: null,
                        description: null,
                        previewImage: null,
                        contentFromUser: true,
                        contentAboutUser: false
                    };

                    const newItem: ImportItem = {
                        ...item,
                        uploadId: uploadLinkres ? uploadLinkres.uploadId : undefined,
                        contentRes: importRes
                    };

                    updateQueue(newItem);
                } else if (isItemTargetPDF && "files" in item && item.files?.length) {
                    // PDF file import
                    const importRes = await onFileChoosenHandler(item.files[0]);

                    const newItem: ImportItem = {
                        ...item,
                        contentRes: importRes
                            ? {
                                  ...importRes,
                                  title: item.files[0].name.replace(".pdf", "") || "",
                                  description: ""
                              }
                            : undefined
                    };

                    updateQueue(newItem);
                } else if ((isItemTargetWebsite || isItemTargetEmail || isItemTargetImage) && "files" in item && item.files?.length) {
                    // Website or email import via screenshots
                    const uploadImages = async () => {
                        if (!item?.files) return;

                        setUploadProgressBar(true);
                        setFileUploadTotal(item.files.length);

                        const imageDimensions = item.files.map(() => ({ width: 0, height: 0 }));

                        for (let index = 0; index < imageDimensions.length; index++) {
                            try {
                                var _URL = window.URL || window.webkitURL;

                                const img = new Image();
                                var objectUrl = _URL.createObjectURL(item.files[index]);
                                img.onload = function () {
                                    //@ts-ignore
                                    imageDimensions[index].height = this.height;
                                    //@ts-ignore
                                    imageDimensions[index].width = this.width;
                                };
                                img.src = objectUrl;
                            } catch {}
                        }

                        const uploadPromises = item
                            .files!.map((file) =>
                                onTemporaryImageUploadHandler(file).then((file) => {
                                    setFileUploadDone((curr) => curr + 1);
                                    return file;
                                })
                            )
                            .map((p) => p!.catch(() => ""));

                        const allImageSrcs = await Promise.all(uploadPromises);
                        const allGoodImageSrcs = allImageSrcs.filter((it) => !!it.length);

                        let content: Website | Email | ImageContentType | undefined;

                        if (isItemTargetWebsite) {
                            content = {
                                type: ContentDetailType.Website,
                                description: "",
                                screenshots: allGoodImageSrcs.map((img, index) => ({
                                    width: imageDimensions[index].width,
                                    height: imageDimensions[index].height,
                                    src: img
                                })),
                                domain: ""
                            } as Website;
                        } else if (isItemTargetEmail) {
                            content = {
                                type: ContentDetailType.Email,
                                description: "",
                                screenshots: allGoodImageSrcs.map((img, index) => ({
                                    width: imageDimensions[index].width,
                                    height: imageDimensions[index].height,
                                    src: img
                                }))
                            } as Email;
                        } else if (isItemTargetImage) {
                            content = {
                                type: ContentDetailType.Image,
                                html: "",
                                screenshots: allGoodImageSrcs.map((img, index) => ({
                                    width: imageDimensions[index].width,
                                    height: imageDimensions[index].height,
                                    src: img
                                }))
                            } as ImageContentType;
                        }

                        if (!content) return;

                        const importRes: ImportResponse = {
                            content: content,
                            type: isItemTargetImage ? ContentType.Image : isItemTargetWebsite ? ContentType.Website : ContentType.Email,
                            url: null,
                            date: null,
                            title: null,
                            text: null,
                            source: null,
                            description: null,
                            previewImage: allGoodImageSrcs.length ? allGoodImageSrcs[0] : null,
                            contentFromUser: true,
                            contentAboutUser: false
                        };

                        const newItem: ImportItem = {
                            ...item,
                            contentRes: importRes
                        };

                        setUploadProgressBar(false);

                        updateQueue(newItem);
                    };

                    uploadImages();
                }
            } catch (e: any) {
                setUploadProgressBar(false);
                updateQueue(item);
            }
        };

        const inFlightItems = itemsFetched.length + itemsFetching.length;

        if (inFlightItems < MAX_CONCURRENT_FETCHES && itemsToImport.length) {
            const item = itemsToImport[0];
            if (item) {
                setItemsFetching((its) => [...its, item]);
                fetchImportResponse(item);
                setItemsToImport((its) => its.filter((it) => it.id !== item.id));
            }
        }

        const hasItemsToProcess = !!itemsToImport.length || !!itemsFetching.length || !!itemsFetched.length;
        if (!hasItemsToProcess && stage === AddContentWizardStage.AddContentProcess) {
            onStageChange({ stage: AddContentWizardStage.AddContentComplete, isConfirmingItem: false, isCheckingDuplicate: false });
            onContentAddComplete();
            setStage(AddContentWizardStage.AddContentComplete);
        }
    }, [itemsToImport, itemsFetched, itemsFetching]);

    const itemsToProcessAmount = itemsToImport.length + itemsFetching.length + itemsFetched.length;

    return (
        <>
            {stage === AddContentWizardStage.AddContentImport && (
                <>
                    <AddContentImport
                        onURLSubmit={(items) => {
                            const domainList: string[] = [];

                            for (let index = 0; index < items.length; index++) {
                                const it = items[index];
                                try {
                                    if (it.url) {
                                        const domain = UrlParser.extractDomain(it.url);

                                        if (domain && !domainList.includes(domain)) domainList.push(domain);
                                    }
                                } catch {}
                            }

                            startBackgroundFetch(items);
                        }}
                        onBackHandler={onBackHandler}
                        importPathTaken={importPathTaken}
                        url={suggestedUrl}
                        onJumpToPDF={onJumpToPDF}
                        usedPlugin={usedPlugin}
                    />
                </>
            )}
            {stage === AddContentWizardStage.AddContentProcess && (
                <>
                    {showUploadProgressBar && (
                        <VerticalSpacing top={12}>
                            <ProgressBar
                                completed={Math.min(100, parseInt(((fileUploadDone / fileUploadTotal) * 100).toFixed(2)))}
                                speed={Math.round(0.125 * fileUploadTotal * 10) / 10}
                            />
                        </VerticalSpacing>
                    )}
                    {itemsTotal > 1 && (
                        <VerticalSpacing bottom={24}>
                            <div style={{ maxWidth: isHalfSplitLayout ? "47%" : "100%" }}>
                                <ImportProgressWithUrlWrapper>
                                    <div>
                                        <V3HeadlineH5>
                                            Importing: {itemsCompleted + 1} of {itemsTotal}
                                        </V3HeadlineH5>
                                    </div>
                                    <div>
                                        {currProcessingItem && !!currProcessingItem?.url && (
                                            <V3BodyGrey35>
                                                (
                                                <InlineLink
                                                    style={{ textDecoration: "underline", color: "inherit" }}
                                                    href={currProcessingItem?.url}
                                                    target="_blank"
                                                >
                                                    Open original {getContentCopyLabel(currProcessingItem.contentRes?.type || targetContentType)} in
                                                    new window
                                                </InlineLink>
                                                )
                                            </V3BodyGrey35>
                                        )}
                                    </div>
                                </ImportProgressWithUrlWrapper>
                                <VerticalSpacing top={12}>
                                    <ProgressBar
                                        completed={Math.min(100, parseInt(((itemsCompleted / itemsTotal) * 100).toFixed(2)))}
                                        speed={Math.round(0.125 * itemsTotal * 10) / 10}
                                    />
                                </VerticalSpacing>
                            </div>
                        </VerticalSpacing>
                    )}
                    {!currProcessingItem ? (
                        <ImportingContentLoader />
                    ) : currDuplicate ? (
                        <DuplicateCheck
                            item={currDuplicate}
                            importUrl={currProcessingItem.url}
                            onCloseHandler={onCloseHandler}
                            onSkip={itemsTotal > 1 ? advanceToNextItem : undefined}
                            onAddAsNew={() => setDuplicatesResolved((ids) => [...ids, currProcessingItem!.id])}
                            onReplace={async () => {
                                const replaced = onReplaceDuplicate ? await onReplaceDuplicate(currDuplicate) : undefined;
                                // On failure we stay on the duplicate screen so the user can pick again
                                if (replaced) advanceToNextItem();
                            }}
                        />
                    ) : (
                        <AddContentProcess
                            isBulkImport={itemsTotal > 1}
                            currProcessingItem={currProcessingItem}
                            sourcesOptions={sourcesOptions}
                            onImportSubmit={async (data) => {
                                await onManualImportSubmit(data);
                                advanceToNextItem();
                            }}
                            onSkip={itemsTotal > 1 ? advanceToNextItem : undefined}
                            onNextText={itemsToProcessAmount === 1 ? "Continue" : "Add & import next"}
                            targetContentType={getTargetContentTypeBasedonManualImportFlow(importPathTaken)}
                            onTemporaryImageUploadHandler={onTemporaryImageUploadHandler}
                        />
                    )}
                </>
            )}
            {stage === AddContentWizardStage.AddContentComplete && <AddContentResultContainer onCloseHandler={onCloseHandler} items={itemAdded} />}
        </>
    );
};
